import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import styled from '@emotion/styled';
import { getPosts } from '../api/wordpress';
import Cover from '../components/Cover';
import PHQ4Chart from '../components/PHQ4Chart';
import TherapistTimeChart from '../components/TherapistTimeChart';
import HumanAssistantChart from '../components/HumanAssistantChart';
import StudyExplorer from '../components/StudyExplorer';
import UniversityLogos from '../components/UniversityLogos';
import SupportEcosystem from '../components/SupportEcosystem';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useState } from 'react';
import BookDemo from '../components/BookDemo';
import LoadingScreen from '../components/LoadingSpinner';
import { Global, css } from '@emotion/react';

// Helper function to decode HTML entities
const decodeHTML = (html: string) => {
  const txt = document.createElement('textarea');
  txt.innerHTML = html;
  return txt.value;
};

const globalStyles = css`
  body {
    background: #0f0f23;
    color: #fff;
    font-family: 'Satoshi', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    margin: 0;
    padding: 0;
  }
`;

const PageContainer = styled.div`
  background: #0f0f23;
  min-height: 100vh;
`;

const Logo = styled.img`
  width: 120px;
  height: auto;
  margin: 2rem auto;
  display: block;
`;

const PostsSection = styled.section`
  background: #0f0f23;
  padding: 4rem 0;
`;

const PostsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
  padding: 1rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const PostCard = styled.article`
  background: #1a1a2e;
  border: 1px solid #2a2a4a;
  border-radius: 0;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    border-color: #4a4a6a;
    transform: translateY(-2px);
    background: #202040;
  }

  a {
    text-decoration: none;
    color: inherit;
  }
`;

const PostImage = styled.img`
  width: 100%;
  height: 200px;
  object-fit: cover;
`;

const PostContent = styled.div`
  padding: 1.5rem;
`;

const PostTitle = styled.h2`
  margin: 0 0 1rem;
  font-size: 1.25rem;
  font-weight: 700;
  color: #fff;
  font-family: 'Satoshi', sans-serif;
`;

const PostExcerpt = styled.div`
  color: #999;
  font-size: 0.875rem;
  line-height: 1.5;
  font-family: 'Satoshi', sans-serif;

  p {
    margin: 0;
  }
`;

const StatsSection = styled.section`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 4rem 1rem;
  background: #0f0f23;

  @media (max-width: 1280px) {
    width: calc(100% - 2rem);
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 2rem;
  margin-bottom: 3rem;

  > * {
    width: 100%;
    margin: 0;
  }

  @media (max-width: 1024px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-size: 2.25rem;
  font-weight: 700;
  color: #fff;
  margin: 0 auto 3rem;
  max-width: 1200px;
  padding: 0 1rem;
  font-family: 'Satoshi', sans-serif;
  text-transform: none;
  letter-spacing: -0.01em;
`;

const StudySection = styled.section`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 4rem 1rem;
  background: #0f0f23;

  @media (max-width: 1280px) {
    width: calc(100% - 2rem);
  }
`;

// Define an interface for the post object
interface Post {
  id: number;
  slug: string;
  title: {
    rendered: string;
  };
  excerpt: {
    rendered: string;
  };
  _embedded?: {
    'wp:featuredmedia'?: Array<{
      source_url: string;
    }>;
  };
}

function Home() {
  const { data: posts, isLoading } = useQuery({
    queryKey: ['posts'],
    queryFn: async () => {
      const response = await fetch('https://ggtude.com/wp-json/wp/v2/posts?_embed&per_page=3');
      if (!response.ok) throw new Error('Network response was not ok');
      return response.json();
    }
  });

  const [isDemoOpen, setIsDemoOpen] = useState(false);

  if (isLoading) return <LoadingScreen />;

  return (
    <PageContainer>
      <Global styles={globalStyles} />
      <Navbar onDemoOpen={() => setIsDemoOpen(true)} />
      <Cover />
      <UniversityLogos />
      <SupportEcosystem />
      <StatsSection>
        <StatsGrid>
          <PHQ4Chart />
          <TherapistTimeChart />
          <HumanAssistantChart />
        </StatsGrid>
      </StatsSection>
      
      <StudySection>
        <StudyExplorer />
      </StudySection>

      <PostsSection>
        <SectionTitle>Latest Insights</SectionTitle>
        <PostsGrid>
        {posts?.slice(0, 6).map((post: Post) => (
          <PostCard key={post.id}>
            <Link to={`/post/${post.slug}`}>
              <PostImage 
                src={post._embedded?.['wp:featuredmedia']?.[0]?.source_url || '/placeholder.jpg'} 
                alt={decodeHTML(post.title.rendered)}
              />
              <PostContent>
                <PostTitle>{decodeHTML(post.title.rendered)}</PostTitle>
                <PostExcerpt dangerouslySetInnerHTML={{ __html: post.excerpt.rendered }} />
              </PostContent>
            </Link>
          </PostCard>
        ))}
        </PostsGrid>
      </PostsSection>
      <BookDemo isOpen={isDemoOpen} onClose={() => setIsDemoOpen(false)} />
    </PageContainer>
  );
}

export default Home; 